// src/services/api.js
import { ENV_CONFIG, API_ENDPOINTS, VALIDATION } from '../config';

/**
 * regNumber のフォーマットを検証する（7桁数字）
 * @param {string} regNumber
 * @throws {Error} フォーマットが無効な場合
 */
function validateRegNumber(regNumber) {
  if (!regNumber || !VALIDATION.REG_NUMBER_PATTERN.test(String(regNumber))) {
    throw new Error('登録番号の形式が正しくありません。');
  }
}

/**
 * バックエンド意図的エラー（HTTP 200 + error_code）を表す専用エラークラス。
 *
 * CloudFront が 403/409 等の HTTPステータスを HTML エラーページに変換してしまうため、
 * バックエンドはビジネスロジック上のエラーを HTTP 200 + error_code フィールドで返す。
 *
 * error_code 一覧:
 *   service_disabled  — サービス対象外（elevator-info なし or evcallEnable=false）
 *   distance_exceeded — エレベータとの距離超過
 *   callid_exhausted  — 呼出ID枯渇（同時接続上限）
 */
class ServiceError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = 'ServiceError';
    this.errorCode = errorCode;
  }
}

class ApiService {
  constructor() {
    this.token = null;
    this.isDebugMode = ENV_CONFIG.DEBUG;
    this.onAuthErrorCallback = null;
  }

  setToken(token) {
    this.token = token;

    if (this.isDebugMode) {
      console.log('デバッグモード: APIトークン設定完了', {
        tokenPreview: token ? token.substring(0, 20) + '...' : 'null'
      });
    }
  }

  // 認証エラー時のコールバックを設定
  setAuthErrorCallback(callback) {
    this.onAuthErrorCallback = callback;
  }

  async authenticatedFetch(url, options = {}) {
    if (!this.token) {
      const errorMsg = "認証トークンが設定されていません";
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    const defaultOptions = {
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      }
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: { ...defaultOptions.headers, ...options.headers }
    };

    if (this.isDebugMode) {
      console.log('デバッグモード API Request:', {
        url,
        method: mergedOptions.method || 'GET',
        headers: {
          ...mergedOptions.headers,
          'Authorization': `Bearer ${this.token.substring(0, 20)}...` // トークンの一部のみ表示
        },
        body: mergedOptions.body
      });
    }

    try {
      const response = await fetch(url, mergedOptions);

      if (response.status === 401) {
        const errorMsg = "認証エラー: セッションが期限切れです";
        console.error(errorMsg, {
          url,
          status: response.status,
          tokenPreview: this.token ? this.token.substring(0, 20) + '...' : 'null'
        });

        if (this.isDebugMode) {
          // デバッグモードでは保存されたトークンをクリア
          localStorage.removeItem('debug_liff_token');
          localStorage.removeItem('debug_liff_profile');
        }

        // 認証エラーコールバックを呼び出し
        if (this.onAuthErrorCallback) {
          this.onAuthErrorCallback();
        }

        throw new Error(errorMsg);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();

        if (this.isDebugMode) {
          console.log('デバッグモード API Response:', {
            url,
            status: response.status,
            data: data
          });
        }

        if (!response.ok) {
          const errorMessage = data.error || data.message || `APIリクエストが失敗しました (ステータス: ${response.status})`;
          throw new Error(errorMessage);
        }
        return data;
      }

      if (!response.ok) {
        const errorMessage = `APIリクエストが失敗しました (ステータス: ${response.status})`;
        console.error(errorMessage, { url, status: response.status });
        throw new Error(errorMessage);
      }
      return response;

    } catch (error) {
      if (this.isDebugMode) {
        console.error('デバッグモード API Error:', {
          url,
          error: error.message,
          name: error.name,
          stack: error.stack
        });
      }

      // ネットワークエラーなどの場合
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('ネットワークエラー: APIサーバーに接続できません');
      }

      throw error;
    }
  }

  // 既存のメソッドはそのまま維持
  async fetchMyElevators() {
    try {
      const data = await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/my-elevators`);

      // レスポンス構造: { elevators: [...], latestCall: {...}, termsAccepted, tutorialShown }
      const elevators = (data.elevators || []).map(item => {
        const elevator = {
          name: item.M.name.S,
          regNumber: item.M.toroku.S
        };

        // lastcallが存在する場合は追加
        if (item.M.lastcall && item.M.lastcall.M) {
          elevator.lastCall = {
            startFloor: item.M.lastcall.M.startFloor?.S || null,
            destFloor: item.M.lastcall.M.destFloor?.S || null,
            handicapped: item.M.lastcall.M.handicapped?.BOOL || false,
            updatedAt: item.M.lastcall.M.updatedAt?.S || null
          };
        } else {
          elevator.lastCall = null;
        }

        // notifyが存在する場合は追加（デフォルトはfalse）
        elevator.notify = item.M.notify?.BOOL ?? false;

        return elevator;
      });

      // latestCallから現在選択中のエレベータ登録番号を取得
      const currentRegNumber = data.latestCall?.S || null;

      return {
        elevators,
        currentRegNumber,
        termsAccepted: data.termsAccepted || false,
        tutorialShown: data.tutorialShown || false
      };
    } catch (error) {
      throw new Error("エレベータリストの取得に失敗しました。");
    }
  }

  async fetchElevatorDetails(regNumber) {
    validateRegNumber(regNumber);
    let url = `${API_ENDPOINTS.BASE}/elevator-details?regno=${encodeURIComponent(regNumber)}`;

    try {
      const data = await this.authenticatedFetch(url);

      // バックエンドが意図的エラー（200 + error_code）を返した場合
      if (data.error_code) {
        throw new ServiceError(data.error_code, data.error || 'エレベータ情報の取得に失敗しました');
      }

      // nextStopDate: 文字列で来た場合はJSONパースしてオブジェクト化
      let nextStopDate = data.nextStopDate;
      if (typeof nextStopDate === 'string') {
        try { nextStopDate = JSON.parse(nextStopDate); } catch { nextStopDate = null; }
      }

      const parsedData = {
        regNumber: data.regNumber || regNumber,
        callev_available: data.callev_available !== undefined ? data.callev_available : true,
        status_available: data.status_available !== undefined ? data.status_available : false,
        stopdate_available: data.stopdate_available !== undefined ? data.stopdate_available : false,
        passwordProtected: data.passwordProtected || false,
        status: data.status || { title: '情報なし', message: '' },
        nextStopDate: nextStopDate || null,
        notificationEnabled: data.notificationEnabled || false,
        wheelchair: data.wheelchair || false,
        floors: (data.floors || []).map(floorItem => ({
          floor: floorItem.floor || '',
          frontName: floorItem.frontName || '',
          rear: floorItem.rear || '0',
          rearName: floorItem.rearName || ''
        }))
      };

      if (!parsedData.floors || parsedData.floors.length === 0) {
        return null;
      }
      return parsedData;
    } catch (error) {
      // バックエンド意図的エラーはそのまま伝播（useApi.js でハンドリング）
      if (error instanceof ServiceError) throw error;
      throw new Error(`エレベータ情報の取得に失敗しました`);
    }
  }

  async fetchCallToken(regNumber) {
    validateRegNumber(regNumber);
    const data = await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/call-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regNumber })
    });

    // バックエンドが意図的エラー（200 + error_code）を返した場合
    if (data.error_code) {
      throw new ServiceError(data.error_code, data.error || 'トークン取得に失敗しました');
    }

    return data.callToken;
  }

  async callElevator(regNumber, startFloor, destFloor, isHandicapped, position) {
    validateRegNumber(regNumber);
    const payload = {
      regNumber: regNumber,
      startFloor: startFloor,
      destFloor: destFloor,
      departure_rear: "0",
      destination_rear: "0",
      handicapped: isHandicapped,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };

    if (!this.token) {
      throw new Error("認証トークンが設定されていません");
    }

    try {
      // 1. エレベータ呼出用ワンタイムトークンを取得
      const callToken = await this.fetchCallToken(regNumber);

      // 2. ワンタイムトークンでエレベータ呼出 API を呼び出す
      const response = await fetch(`${API_ENDPOINTS.BASE}/call-elevator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${callToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // 429 Too Many Requests の場合は特別処理
      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        return {
          success: false,
          isRateLimited: true,
          status: 429,
          message: data.message || 'しばらくお待ちください。'
        };
      }

      // 401 認証エラー
      if (response.status === 401) {
        if (this.onAuthErrorCallback) {
          this.onAuthErrorCallback();
        }
        throw new Error("認証エラー: セッションが期限切れです");
      }

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        return {
          success: false,
          status: 403,
          errorCode: data.error_code || '',
          message: data.error || data.message || 'アクセスが拒否されました。'
        };
      }

      const data = await response.json();

      // バックエンドが意図的エラー（200 + error_code）を返した場合
      if (data.error_code) {
        throw new ServiceError(data.error_code, data.error || data.message || 'エラーが発生しました。');
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || `エレベータの呼び出しに失敗しました (ステータス: ${response.status})`);
      }

      return data;
    } catch (error) {
      if (error.message.includes('認証エラー')) throw error;
      // バックエンド意図的エラー → errorCode 付きで返す（useApi.js でハンドリング）
      if (error instanceof ServiceError) {
        return { success: false, errorCode: error.errorCode, message: error.message };
      }
      throw new Error(`しばらくしてから、再度お試しください。`);
    }
  }

  async addNewElevator(regNumber, name) {
    validateRegNumber(regNumber);
    const sanitizedName = name.trim().substring(0, 20);
    if (!sanitizedName) {
      throw new Error('エレベータ名が空です');
    }
    await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/my-elevators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        regNumber: regNumber,
        name: sanitizedName
      })
    });
  }

  async updateElevatorName(regNumber, newName) {
    validateRegNumber(regNumber);
    const sanitizedName = newName.trim().substring(0, 20);
    if (!sanitizedName) {
      throw new Error('エレベータ名が空です');
    }
    await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/my-elevators`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        regNumber: regNumber,
        name: sanitizedName
      })
    });
  }

  async updateNotificationSetting(regNumber, notify) {
    validateRegNumber(regNumber);
    await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/elevator-details`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        regNumber: regNumber,
        notify: notify
      })
    });
  }

  async deleteElevator(regNumber) {
    validateRegNumber(regNumber);
    await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/my-elevators?regNumber=${encodeURIComponent(regNumber)}`, {
      method: 'DELETE'
    });
  }

  async updateMyElevatorsOrder(order) {
    try {
      const data = await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/my-elevators`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order }),
      });

      return data;
    } catch (error) {
      throw new Error(`並び替えの保存に失敗しました`);
    }
  }

  async updateUserPreferences(preferences) {
    try {
      await this.authenticatedFetch(`${API_ENDPOINTS.BASE}/user-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });
    } catch (error) {
      console.error('プリファレンス更新失敗:', error);
    }
  }
}

const apiService = new ApiService();
export default apiService;